import { NetworkError, toApiError, UnknownResultError } from './errors.js';
import type { CommandIntent } from './idempotency.js';
import { uuidV4 } from './ids.js';

/**
 * The SAAY fetch wrapper (AC §3, §4.9, §7; FTD §14). It owns, for every call:
 *
 * - `Authorization: Bearer` from an injected, in-memory {@link TokenProvider} (T-01);
 * - one `Idempotency-Key` per {@link CommandIntent}, reused for transport retries (AC §7.1);
 * - a fresh `X-Request-ID` (UUID v4) per HTTP request; the server's own ID is read back (AC §3.3);
 * - `X-Device-ID` where the route requires it (AC §7.4);
 * - the error envelope, parsed into {@link ApiError};
 * - `401`: one silent refresh, then one retry; a second failure ends the session;
 * - `Retry-After`: a short wait is retried once automatically, a longer one is reported.
 *
 * It never logs, stores, or caches a token or a response body.
 */

export interface TokenProvider {
  /** The current access token, held in memory only, or `null` when signed out. */
  getAccessToken(): string | null | Promise<string | null>;
  /** Silent renew (refresh-token rotation). Resolves the new token, or `null` if the session ended. */
  refreshAccessToken(): Promise<string | null>;
}

export type DeviceIdMode = 'none' | 'optional' | 'required';

export type QueryValue = string | number | boolean | readonly (string | number)[] | undefined;

export interface RequestOptions {
  /** Send the Bearer token (default `true`). Public routes (health) pass `false`. */
  readonly auth?: boolean;
  /** `X-Device-ID` handling for this route (default `optional`). */
  readonly deviceId?: DeviceIdMode;
  readonly query?: Readonly<Record<string, QueryValue>>;
  readonly signal?: AbortSignal;
}

export interface CommandOptions extends RequestOptions {
  readonly body?: unknown;
  /** The user intent that owns the `Idempotency-Key` (required for state-changing POST, AC §7.1). */
  readonly intent: CommandIntent;
}

export interface MutationOptions extends RequestOptions {
  readonly body?: unknown;
  /**
   * The user intent that owns the `Idempotency-Key` of a DELETE that the contract makes idempotent
   * (for example AC §27.1 `DELETE …/me/sessions/{sessionId}`); omitted, no key is sent.
   */
  readonly intent?: CommandIntent;
}

export interface ApiResult<T> {
  readonly data: T;
  /** `Location` of a created resource (`201`). */
  readonly location: string | null;
  /** `true` when the body is a stored idempotent replay (`Idempotency-Replayed`). */
  readonly replayed: boolean;
  /** The server's correlation ID (`X-Request-ID`), which support can look up. */
  readonly requestId: string | null;
  readonly status: number;
}

export interface ApiClientOptions {
  /** Retries after `Retry-After` only when the wait is at most this many seconds (default 2). */
  readonly autoRetryMaxSeconds?: number;
  /** The API root including the version prefix, for example `http://127.0.0.1:3000/v1`. */
  readonly baseUrl: string;
  readonly deviceIdProvider?: () => string | Promise<string>;
  readonly fetch?: typeof fetch;
  /** Called once when the session cannot be renewed (401 after refresh, FTD §11 session expired). */
  readonly onSessionExpired?: () => void;
  readonly requestId?: () => string;
  readonly sleep?: (milliseconds: number) => Promise<void>;
  /** Per-request timeout; a command that times out is an unknown result (default 30 s). */
  readonly timeoutMs?: number;
  readonly tokenProvider?: TokenProvider;
}

export interface ApiClient {
  delete<T = undefined>(path: string, options?: MutationOptions): Promise<ApiResult<T>>;
  get<T>(path: string, options?: RequestOptions): Promise<ApiResult<T>>;
  patch<T>(path: string, options: MutationOptions): Promise<ApiResult<T>>;
  post<T>(path: string, options: CommandOptions): Promise<ApiResult<T>>;
  /** A side-effect-free POST that takes no key (previews, `notifications/{id}/read`, AC §7.1). */
  postWithoutKey<T>(path: string, options?: MutationOptions): Promise<ApiResult<T>>;
}

type Method = 'GET' | 'POST' | 'PATCH' | 'DELETE';

interface PreparedRequest {
  readonly body: string | undefined;
  readonly idempotencyKey: string | undefined;
  readonly method: Method;
  readonly options: RequestOptions;
  readonly url: string;
}

function defaultSleep(milliseconds: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, milliseconds);
  });
}

function buildUrl(baseUrl: string, path: string, query: RequestOptions['query']): string {
  const url = `${baseUrl.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined) {
      continue;
    }
    // Enum and ID filters are comma-separated (AC §4.8).
    params.set(key, Array.isArray(value) ? value.join(',') : String(value));
  }

  const search = params.toString();
  return search === '' ? url : `${url}?${search}`;
}

async function readBody(response: Response): Promise<unknown> {
  if (response.status === 204) {
    return undefined;
  }
  const text = await response.text();
  if (text === '') {
    return undefined;
  }
  const type = response.headers.get('content-type') ?? '';
  if (!type.includes('json')) {
    return text;
  }
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

/**
 * The request's signal: the caller's signal (if any) combined with a timeout. `AbortSignal.timeout`
 * needs Safari 16 and `AbortSignal.any` Safari 17.4, so older iPhones combine them by hand; without
 * this every request on those phones threw before it was sent. `dispose` clears the timer.
 */
export function requestSignal(
  caller: AbortSignal | undefined,
  timeoutMs: number,
): { readonly signal: AbortSignal; readonly dispose: () => void } {
  const native = AbortSignal as typeof AbortSignal & {
    any?: (signals: AbortSignal[]) => AbortSignal;
    timeout?: (milliseconds: number) => AbortSignal;
  };
  if (
    typeof native.timeout === 'function' &&
    (caller === undefined || typeof native.any === 'function')
  ) {
    const timeout = native.timeout(timeoutMs);
    return {
      dispose: () => undefined,
      signal: caller === undefined ? timeout : native.any!([caller, timeout]),
    };
  }
  const controller = new AbortController();
  const timer = setTimeout(() => {
    controller.abort(new DOMException('The request timed out.', 'TimeoutError'));
  }, timeoutMs);
  const onCallerAbort = () => controller.abort(caller?.reason);
  if (caller?.aborted === true) {
    onCallerAbort();
  } else {
    caller?.addEventListener('abort', onCallerAbort, { once: true });
  }
  return {
    dispose: () => {
      clearTimeout(timer);
      caller?.removeEventListener('abort', onCallerAbort);
    },
    signal: controller.signal,
  };
}

function isAbortFromCaller(error: unknown, signal: AbortSignal | undefined): boolean {
  return signal?.aborted === true && (error as { name?: string } | null)?.name === 'AbortError';
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  // Resolved per call, so interceptors installed later (MSW in tests) are honored.
  const fetchImpl: typeof fetch = options.fetch ?? ((input, init) => globalThis.fetch(input, init));
  const sleep = options.sleep ?? defaultSleep;
  const timeoutMs = options.timeoutMs ?? 30_000;
  const autoRetryMaxSeconds = options.autoRetryMaxSeconds ?? 2;
  const requestId = options.requestId ?? uuidV4;

  async function headersFor(request: PreparedRequest, token: string | null): Promise<Headers> {
    const headers = new Headers({
      Accept: 'application/json',
      'Accept-Language': 'fa',
      'X-Request-ID': requestId(),
    });

    if (request.body !== undefined) {
      headers.set('Content-Type', 'application/json');
    }
    if (request.idempotencyKey !== undefined) {
      headers.set('Idempotency-Key', request.idempotencyKey);
    }
    if (token !== null) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const mode = request.options.deviceId ?? 'optional';
    if (mode !== 'none') {
      if (options.deviceIdProvider === undefined) {
        if (mode === 'required') {
          throw new Error(
            'X-Device-ID is required for this route but no device ID provider is set.',
          );
        }
      } else {
        headers.set('X-Device-ID', await options.deviceIdProvider());
      }
    }
    return headers;
  }

  async function send(request: PreparedRequest, token: string | null): Promise<Response> {
    const headers = await headersFor(request, token);
    const { dispose, signal } = requestSignal(request.options.signal, timeoutMs);

    try {
      return await fetchImpl(request.url, {
        body: request.body ?? null,
        cache: 'no-store',
        credentials: 'omit',
        headers,
        method: request.method,
        signal,
      });
    } catch (error) {
      if (isAbortFromCaller(error, request.options.signal)) {
        throw error;
      }
      // A mutation that may have reached the server has an unknown result (FTD §2.4, §14.2).
      if (request.method !== 'GET' && request.idempotencyKey !== undefined) {
        throw new UnknownResultError(request.idempotencyKey, { cause: error });
      }
      if (request.method !== 'GET') {
        throw new UnknownResultError('', { cause: error });
      }
      throw new NetworkError({ cause: error });
    } finally {
      dispose();
    }
  }

  async function execute<T>(request: PreparedRequest): Promise<ApiResult<T>> {
    const useAuth = request.options.auth !== false && options.tokenProvider !== undefined;
    let token = useAuth ? await options.tokenProvider!.getAccessToken() : null;
    let refreshed = false;
    let waited = false;

    for (;;) {
      const response = await send(request, token);
      const body = await readBody(response);

      if (response.ok) {
        return {
          data: body as T,
          location: response.headers.get('location'),
          replayed: response.headers.get('idempotency-replayed') === 'true',
          requestId: response.headers.get('x-request-id'),
          status: response.status,
        };
      }

      const error = toApiError(response.status, body, response.headers);

      // 401: one silent refresh, then one retry.
      if (response.status === 401 && useAuth) {
        if (!refreshed) {
          refreshed = true;
          token = await options.tokenProvider!.refreshAccessToken().catch(() => null);
          if (token !== null) {
            continue;
          }
        }
        options.onSessionExpired?.();
        throw error;
      }

      // Retry-After: a short wait is retried once with the same key; longer waits go to the UI.
      const retryAfter = error.retryAfterSeconds;
      const waitable =
        response.status === 429 ||
        response.status === 503 ||
        error.code === 'IDEMPOTENCY_REQUEST_IN_PROGRESS';
      const safeToRepeat = request.method === 'GET' || request.idempotencyKey !== undefined;
      if (
        waitable &&
        safeToRepeat &&
        !waited &&
        retryAfter !== undefined &&
        retryAfter <= autoRetryMaxSeconds
      ) {
        waited = true;
        await sleep(retryAfter * 1000);
        continue;
      }

      throw error;
    }
  }

  function prepare(
    method: Method,
    path: string,
    requestOptions: RequestOptions & { readonly body?: unknown },
    idempotencyKey: string | undefined,
  ): PreparedRequest {
    return {
      body: requestOptions.body === undefined ? undefined : JSON.stringify(requestOptions.body),
      idempotencyKey,
      method,
      options: requestOptions,
      url: buildUrl(options.baseUrl, path, requestOptions.query),
    };
  }

  return {
    delete: (path, requestOptions = {}) =>
      execute(
        prepare(
          'DELETE',
          path,
          requestOptions,
          requestOptions.intent?.keyFor({ body: requestOptions.body ?? null, path }),
        ),
      ),
    get: (path, requestOptions = {}) => execute(prepare('GET', path, requestOptions, undefined)),
    patch: (path, requestOptions) => execute(prepare('PATCH', path, requestOptions, undefined)),
    post: (path, requestOptions) =>
      execute(
        prepare(
          'POST',
          path,
          requestOptions,
          requestOptions.intent.keyFor({ body: requestOptions.body ?? null, path }),
        ),
      ),
    postWithoutKey: (path, requestOptions = {}) =>
      execute(prepare('POST', path, requestOptions, undefined)),
  };
}
